import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Building2, 
  Plus, 
  Users, 
  Settings,
  Trash2,
  UserPlus,
  UserMinus
} from 'lucide-react';
import { PageContainer } from '../../components/ui/PageContainer';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { api } from '../../lib/api';

interface Entity {
  id: string;
  name: string;
  document: string;
  type: 'COMPANY' | 'INDIVIDUAL';
}

interface EconomicGroupMember {
  id: string;
  entityId: string;
  entity: Entity;
  role: 'PARENT' | 'SUBSIDIARY' | 'AFFILIATE' | 'MEMBER';
  addedAt: string;
}

interface EconomicGroup {
  id: string;
  name: string;
  description?: string;
  parentEntityId: string;
  parentEntity: Entity;
  members: EconomicGroupMember[];
  dailyLimit?: string | null;
  monthlyLimit?: string | null;
  perTransactionLimit?: string | null;
  intraGroupFeeDiscount: number;
  isActive: boolean;
  createdAt: string;
}

interface GroupForm {
  name: string;
  description: string;
  parentEntityId: string;
  dailyLimit: string;
  monthlyLimit: string;
  intraGroupFeeDiscount: string;
}

const initialForm: GroupForm = {
  name: '',
  description: '',
  parentEntityId: '',
  dailyLimit: '',
  monthlyLimit: '',
  intraGroupFeeDiscount: '0',
};

export default function EconomicGroups() {
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [showLimitsModal, setShowLimitsModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<EconomicGroup | null>(null);
  const [form, setForm] = useState<GroupForm>(initialForm);
  const [selectedEntityToAdd, setSelectedEntityToAdd] = useState('');
  const [memberRole, setMemberRole] = useState<'MEMBER' | 'SUBSIDIARY' | 'AFFILIATE'>('MEMBER');

  // Fetch groups
  const { data: groups, isLoading } = useQuery({
    queryKey: ['economic-groups'],
    queryFn: async () => {
      const response = await api.get('/economic-groups');
      return response.data as EconomicGroup[];
    },
  });

  // Fetch entities for selection
  const { data: entities } = useQuery({
    queryKey: ['entities'],
    queryFn: async () => {
      const response = await api.get('/entities');
      return response.data as Entity[];
    },
  });

  // Create group mutation
  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await api.post('/economic-groups', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['economic-groups'] });
      setShowCreateModal(false);
      setForm(initialForm);
    },
  });

  // Update group mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const response = await api.put(`/economic-groups/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['economic-groups'] });
      setShowLimitsModal(false);
      setSelectedGroup(null);
    },
  });

  // Delete group mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/economic-groups/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['economic-groups'] });
    },
  });

  // Add member mutation
  const addMemberMutation = useMutation({
    mutationFn: async ({ groupId, entityId, role }: { groupId: string; entityId: string; role: string }) => {
      const response = await api.post(`/economic-groups/${groupId}/members`, { entityId, role });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['economic-groups'] });
      setSelectedEntityToAdd('');
    },
  });

  // Remove member mutation
  const removeMemberMutation = useMutation({
    mutationFn: async ({ groupId, entityId }: { groupId: string; entityId: string }) => {
      await api.delete(`/economic-groups/${groupId}/members/${entityId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['economic-groups'] });
    },
  });

  const handleCreate = () => {
    const data = {
      name: form.name,
      description: form.description || undefined,
      parentEntityId: form.parentEntityId,
      dailyLimit: form.dailyLimit ? Math.round(parseFloat(form.dailyLimit) * 100) : undefined,
      monthlyLimit: form.monthlyLimit ? Math.round(parseFloat(form.monthlyLimit) * 100) : undefined,
      intraGroupFeeDiscount: parseFloat(form.intraGroupFeeDiscount) || 0,
    };
    createMutation.mutate(data);
  };

  const handleUpdateLimits = () => {
    if (!selectedGroup) return;
    const data = {
      dailyLimit: form.dailyLimit ? Math.round(parseFloat(form.dailyLimit) * 100) : undefined,
      monthlyLimit: form.monthlyLimit ? Math.round(parseFloat(form.monthlyLimit) * 100) : undefined,
      intraGroupFeeDiscount: parseFloat(form.intraGroupFeeDiscount) || 0,
    };
    updateMutation.mutate({ id: selectedGroup.id, data });
  };

  const openLimitsModal = (group: EconomicGroup) => {
    setSelectedGroup(group);
    setForm({
      ...initialForm,
      dailyLimit: group.dailyLimit ? (Number(group.dailyLimit) / 100).toString() : '',
      monthlyLimit: group.monthlyLimit ? (Number(group.monthlyLimit) / 100).toString() : '',
      intraGroupFeeDiscount: group.intraGroupFeeDiscount.toString(),
    });
    setShowLimitsModal(true);
  };

  const formatCurrency = (value: string | number) => {
    const numValue = typeof value === 'string' ? Number(value) : value;
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(numValue / 100);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('pt-BR');
  };

  const getRoleBadge = (role: string) => {
    const variants: Record<string, 'primary' | 'info' | 'warning'> = {
      PARENT: 'primary',
      SUBSIDIARY: 'info',
      AFFILIATE: 'warning',
      MEMBER: 'info',
    };
    const labels: Record<string, string> = {
      PARENT: 'Matriz',
      SUBSIDIARY: 'Filial',
      AFFILIATE: 'Coligada',
      MEMBER: 'Membro',
    };
    return <Badge variant={variants[role] || 'info'}>{labels[role] || role}</Badge>;
  };

  // Get available entities (not already in the selected group)
  const getAvailableEntities = () => {
    if (!selectedGroup || !entities) return [];
    const memberIds = selectedGroup.members.map(m => m.entityId);
    memberIds.push(selectedGroup.parentEntityId);
    return entities.filter(e => !memberIds.includes(e.id));
  };

  return (
    <PageContainer
      title="Grupos Econômicos"
      description="Gerencie grupos de entidades relacionadas"
      icon={Building2}
    >
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div />
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Novo Grupo
        </Button>
      </div>

      {/* Groups List */}
      {isLoading ? (
        <div className="text-center py-8 text-surface-400">Carregando...</div>
      ) : groups && groups.length > 0 ? (
        <div className="grid gap-6">
          {groups.map((group) => (
            <Card key={group.id}>
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-primary-500/20 flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-primary-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-surface-100">{group.name}</h3>
                      {group.description && (
                        <p className="text-sm text-surface-400">{group.description}</p>
                      )}
                      <p className="text-sm text-surface-500">
                        Matriz: {group.parentEntity.name}
                      </p>
                    </div>
                  </div>
                  <Badge variant={group.isActive ? 'success' : 'error'}>
                    {group.isActive ? 'Ativo' : 'Inativo'}
                  </Badge>
                </div>

                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="p-3 bg-surface-800 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <Users className="w-4 h-4 text-accent-400" />
                      <span className="text-xs text-surface-400">Membros</span>
                    </div>
                    <p className="text-lg font-bold text-surface-100">
                      {group.members.length}
                    </p>
                  </div>
                  <div className="p-3 bg-surface-800 rounded-lg">
                    <span className="text-xs text-surface-400">Limite Diário</span>
                    <p className="text-lg font-bold text-surface-100">
                      {group.dailyLimit ? formatCurrency(group.dailyLimit) : 'Sem limite'}
                    </p>
                  </div>
                  <div className="p-3 bg-surface-800 rounded-lg">
                    <span className="text-xs text-surface-400">Desconto Intra-grupo</span>
                    <p className="text-lg font-bold text-success">
                      {group.intraGroupFeeDiscount}%
                    </p>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSelectedGroup(group);
                      setShowMembersModal(true);
                    }}
                  >
                    <Users className="w-4 h-4 mr-1" />
                    Membros
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openLimitsModal(group)}
                  >
                    <Settings className="w-4 h-4 mr-1" />
                    Limites
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => deleteMutation.mutate(group.id)}
                  >
                    <Trash2 className="w-4 h-4 text-error" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <div className="p-8 text-center text-surface-400">
            Nenhum grupo econômico cadastrado
          </div>
        </Card>
      )}

      {/* Create Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setForm(initialForm);
        }}
        title="Novo Grupo Econômico"
      >
        <div className="space-y-4">
          <Input
            label="Nome do grupo"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Ex: Grupo ABC"
          />
          
          <Input
            label="Descrição (opcional)"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Descrição do grupo"
          />
          
          <Select
            label="Entidade Matriz"
            value={form.parentEntityId}
            onChange={(e) => setForm({ ...form, parentEntityId: e.target.value })}
            options={[
              { value: '', label: 'Selecione...' },
              ...(entities?.filter(e => e.type === 'COMPANY').map(e => ({
                value: e.id,
                label: `${e.name} (${e.document})`,
              })) || []),
            ]}
          />
          
          <Input
            label="Limite diário (opcional)"
            type="number"
            value={form.dailyLimit}
            onChange={(e) => setForm({ ...form, dailyLimit: e.target.value })}
            placeholder="0,00"
            min="0"
            step="0.01"
          />
          
          <Input
            label="Limite mensal (opcional)"
            type="number"
            value={form.monthlyLimit}
            onChange={(e) => setForm({ ...form, monthlyLimit: e.target.value })}
            placeholder="0,00"
            min="0"
            step="0.01"
          />
          
          <Input
            label="Desconto de taxa intra-grupo (%)"
            type="number"
            value={form.intraGroupFeeDiscount}
            onChange={(e) => setForm({ ...form, intraGroupFeeDiscount: e.target.value })}
            placeholder="0"
            min="0"
            max="100"
          />

          <div className="flex gap-4 pt-4">
            <Button
              variant="secondary"
              onClick={() => {
                setShowCreateModal(false);
                setForm(initialForm);
              }}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleCreate}
              disabled={!form.name || !form.parentEntityId || createMutation.isPending}
              className="flex-1"
            >
              {createMutation.isPending ? 'Criando...' : 'Criar'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Members Modal */}
      <Modal
        isOpen={showMembersModal}
        onClose={() => {
          setShowMembersModal(false);
          setSelectedGroup(null);
          setSelectedEntityToAdd('');
        }}
        title={`Membros - ${selectedGroup?.name}`}
        size="lg"
      >
        {selectedGroup && (
          <div className="space-y-4">
            {/* Add member */}
            <div className="p-4 bg-surface-800 rounded-lg">
              <h4 className="text-sm font-medium text-surface-100 mb-3">Adicionar membro</h4>
              <div className="flex gap-2">
                <Select
                  value={selectedEntityToAdd}
                  onChange={(e) => setSelectedEntityToAdd(e.target.value)}
                  options={[
                    { value: '', label: 'Selecione uma entidade...' },
                    ...getAvailableEntities().map(e => ({
                      value: e.id,
                      label: `${e.name} (${e.document})`,
                    })),
                  ]}
                  className="flex-1"
                />
                <Select
                  value={memberRole}
                  onChange={(e) => setMemberRole(e.target.value as 'MEMBER' | 'SUBSIDIARY' | 'AFFILIATE')}
                  options={[
                    { value: 'MEMBER', label: 'Membro' },
                    { value: 'SUBSIDIARY', label: 'Filial' },
                    { value: 'AFFILIATE', label: 'Coligada' },
                  ]}
                />
                <Button
                  onClick={() => addMemberMutation.mutate({
                    groupId: selectedGroup.id,
                    entityId: selectedEntityToAdd,
                    role: memberRole,
                  })}
                  disabled={!selectedEntityToAdd || addMemberMutation.isPending}
                >
                  <UserPlus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Members list */}
            <div className="space-y-2">
              {/* Parent entity */}
              <div className="flex items-center justify-between p-3 bg-primary-500/10 border border-primary-500/30 rounded-lg">
                <div className="flex items-center gap-3">
                  <Building2 className="w-5 h-5 text-primary-400" />
                  <div>
                    <p className="text-surface-100 font-medium">{selectedGroup.parentEntity.name}</p>
                    <p className="text-sm text-surface-400">{selectedGroup.parentEntity.document}</p>
                  </div>
                </div>
                {getRoleBadge('PARENT')}
              </div>

              {/* Other members */}
              {selectedGroup.members.map((member) => (
                <div
                  key={member.id}
                  className="flex items-center justify-between p-3 bg-surface-800 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <Building2 className="w-5 h-5 text-surface-400" />
                    <div>
                      <p className="text-surface-100 font-medium">{member.entity.name}</p>
                      <p className="text-sm text-surface-400">{member.entity.document}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {getRoleBadge(member.role)}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeMemberMutation.mutate({
                        groupId: selectedGroup.id,
                        entityId: member.entityId,
                      })}
                    >
                      <UserMinus className="w-4 h-4 text-error" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal>

      {/* Limits Modal */}
      <Modal
        isOpen={showLimitsModal}
        onClose={() => {
          setShowLimitsModal(false);
          setSelectedGroup(null);
          setForm(initialForm);
        }}
        title={`Configurar Limites - ${selectedGroup?.name}`}
      >
        <div className="space-y-4">
          <Input
            label="Limite diário"
            type="number"
            value={form.dailyLimit}
            onChange={(e) => setForm({ ...form, dailyLimit: e.target.value })}
            placeholder="0,00"
            min="0"
            step="0.01"
          />
          
          <Input
            label="Limite mensal"
            type="number"
            value={form.monthlyLimit}
            onChange={(e) => setForm({ ...form, monthlyLimit: e.target.value })}
            placeholder="0,00"
            min="0"
            step="0.01"
          />
          
          <Input
            label="Desconto de taxa intra-grupo (%)"
            type="number"
            value={form.intraGroupFeeDiscount}
            onChange={(e) => setForm({ ...form, intraGroupFeeDiscount: e.target.value })}
            placeholder="0"
            min="0"
            max="100"
          />

          <p className="text-sm text-surface-400">
            O desconto de taxa é aplicado automaticamente em transferências entre membros do grupo.
          </p>

          <div className="flex gap-4 pt-4">
            <Button
              variant="secondary"
              onClick={() => {
                setShowLimitsModal(false);
                setSelectedGroup(null);
                setForm(initialForm);
              }}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleUpdateLimits}
              disabled={updateMutation.isPending}
              className="flex-1"
            >
              {updateMutation.isPending ? 'Salvando...' : 'Salvar'}
            </Button>
          </div>
        </div>
      </Modal>
    </PageContainer>
  );
}
